import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { FormControl, Typography } from "@mui/material";
import withStyles from '@mui/styles/withStyles';
import { useTranslation } from "react-i18next";


// import styling
import './RangedRadioFilter.scss';

// interface for value that is to be passed in
// NOTE: Will likely have to change number to String then use function to check and convert to Number
interface valueTypes {
  filter: string;
  eq: number;
  min: number;
  max: number;
}

// interface that defines the types of the props
interface propTypes {
  label: string;
  onChange: any;
  textChange: any;
  value: valueTypes;
  name: string;
}

const StyledRadio = withStyles({
  root: {
    color: "#97999c",
    '&$checked' : {
      color: '#005078',
    }
  },
  checked: {}
})(Radio)

/**
 * This is a filter component which is used in the slide-out filter drawer. It allows the user to determine if they want to
 *    filter for All objects, objects with a certain value, or objects with a value within a set range.
 * @param param The props passed into the component
 *    label { String } | The label to be displayed above this filter component - identifies what the filter is used for
 *    onChange { Function } | A function to be triggered when the radio selection is changed
 *    value { { filter, eq, min, max } : valueTypes } | The value representing the current state of the filter
 *    textChange {} | A function to be triggered when the text input fields are changed
 *    name {} | Identifies the name of the Radio group - used to identify the component on update
 * @returns The RangedRadioFilter
 */
export default function RangedRadioFilter({
  label,
  onChange,
  value,
  textChange,
  name
}: propTypes) {

  
    const { t } = useTranslation();
    const handleTextChange = (event) => {
        const textName = event.target.name;
        const val = event.target.value;
        // create a temp object to hold the new value
        let temp = value;

        // change the temp value to reflect the newly entered text
        temp[textName] = val;

        // Call the text change function passing in an event object parameter
        textChange({target : { name: name, value: temp }});
    }

  const preventPasteNegative = (e) => {
    e.preventDefault();
    return false;
  };  

  const preventMinus = (e) => {
    if (e.code === 'Minus' || e.code === 'NumpadSubtract') {
      e.preventDefault();
    }
  };

  return (
    <div className="radio-filters">
      <FormControl component="fieldset">
        <Typography id="radio-filters-typography" gutterBottom >
            <span className='radio-label'>{label}</span>
          </Typography>
        <RadioGroup value={value.filter} onChange={onChange} name={name}>
          <div className="radio-option">
            <FormControlLabel
              value={"All"}
              control={<StyledRadio />}
              label={"All"}
              labelPlacement="end"
            />
          </div>
          {/* Equal Radio filter */}
          <div className="radio-option">
            <FormControlLabel
              value={"Eq"}
              control={<StyledRadio />}
              label={t('RangedRadioFilter.equal')}
              labelPlacement="end"
            />
            {/* Text input to filter for certain values */}
            <input
              name={`eq`}
              type="number" min="0" onPaste={preventPasteNegative} onKeyPress={preventMinus}
              disabled={value.filter !== "Eq" ? true : false}
              value={value.eq >= 0 ? value.eq : ""}
              onChange={handleTextChange}
              className='eq-input form-states'
            />
          </div>
          {/* Ranged Radio Filter */}
          <div className="radio-option">
            <FormControlLabel
              value={"Range"}
              control={<StyledRadio />}
              label={"From"}
              labelPlacement="end"
            />
            {/* Minimum range */}
            <input
              name={`min`}
              type="number" min="0" onPaste={preventPasteNegative} onKeyPress={preventMinus}
              disabled={value.filter !== "Range" ? true : false}
              value={value.min >= 0 ? value.min : ""}
              onChange={handleTextChange}
              className='range-input-min form-states'
            />
            <span>{t('RangedRadioFilter.to')}</span> 
            {/* Maximum Range */}
            <input
              name={`max`}
              type="number" min="0" onPaste={preventPasteNegative} onKeyPress={preventMinus}
              disabled={value.filter !== "Range" ? true : false}
              value={value.max >= 0 ? value.max : ""}
              onChange={handleTextChange}
              className='range-input-max form-states'
            />
          </div>
        </RadioGroup>
      </FormControl>
    </div>
  );
}
